import React from 'react'
import { useHistory } from 'react-router-dom'

import {
  useGetOpenBatchesQuery,
  useGetPendingExchangeInstructionsInstructionsQuery
} from '../../../../api-services/financial/currencyExchangeApi'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import { setShowAllBatches, selectShowAllBatches } from './exchangeInstructionsSlice'

import { Box, Button, Typography, Paper, Grid, Switch } from '@mui/material'
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridValueGetterParams } from '@mui/x-data-grid'
import InstructionStateChip from './InstructionStateChip'
import StartOutlined from '@mui/icons-material/StartOutlined'
import Refresh from '@mui/icons-material/Refresh'
import HomeIcon from '@mui/icons-material/Home'
import CreateBatch from './CreateBatch'

const ExchangeInstructionBatchesPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const history = useHistory()

  const showAllBatches: boolean = useAppSelector<boolean>(selectShowAllBatches)
  const getOpenBatchesApiResult = useGetOpenBatchesQuery(showAllBatches)
  const getPendingInstructionsApiResult = useGetPendingExchangeInstructionsInstructionsQuery({ })

  const refetchBatches = getOpenBatchesApiResult.refetch
  const refetchInstructions = getPendingInstructionsApiResult.refetch

  const goTo = (location: string): void => {
    history.push(location)
  }

  const handleRefetch = async (): Promise<void> => {
    await refetchBatches()
    await refetchInstructions()
  }

  const handleGoToManage = (batchId: number | undefined): void => {
    goTo(`/app/finance-area/currency-exchange-instructions/batches/${batchId}/edit`)
  }

  const handleBatchFilterSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      dispatch(setShowAllBatches(true))
    } else {
      dispatch(setShowAllBatches(false))
    }
  }

  const batchColumns: GridColDef[] = [
    { field: 'batchId', headerName: 'ID', width: 35, editable: false },
    {
      field: 'fromCurrencyCode',
      headerName: 'From Currency',
      width: 120,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.fromCurrencyCode} name={`${params.row.fromCurrencyCode}`}/>
    },
    {
      field: 'toCurrencyCode',
      headerName: 'to Currency',
      width: 120,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.toCurrencyCode} name={`${params.row.toCurrencyCode}`}/>
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 50,
      editable: false,
      renderCell: params => <InstructionStateChip state={params.row.state}/>
    },
    { field: 'countInstructions', headerName: 'Count', flex: 50, editable: false },
    { field: 'countInstructionsProcessed', headerName: 'Processed', flex: 50, editable: false },
    { field: 'totalFromCurrencyAmount', headerName: 'From Amount', flex: 50, editable: false, align: 'right' },
    { field: 'totalToCurrencyAmount', headerName: 'To Amount', flex: 50, editable: false, align: 'right' },
    { field: 'exchangeRate', headerName: 'Exchange Rate', flex: 50, editable: false, align: 'right' },
    { field: 'createdAtFormatted', headerName: 'Created At', flex: 50, editable: false },
    { field: 'createdByEmailAddress', headerName: 'Created By', flex: 50, editable: false },
    {
      field: 'manageBatch', // Don't repeat ID - does weird things with the ordering of the columns
      headerName: '',
      width: 100,
      valueGetter: (params: GridValueGetterParams): number => params.row.batchId,
      renderCell: (params: GridRenderCellParams<number>) => (
        <Button onClick={() => { handleGoToManage(params.value) }} type={'button'} variant={'text'}>
          <StartOutlined />
        </Button>
      )
    }
  ]

  const instructionColumns: GridColDef[] = [
    { field: 'currencyExchangeInstructionId', headerName: 'ID', width: 35, editable: false },
    {
      field: 'fromCurrencyCode',
      headerName: 'From Currency',
      width: 120,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.fromCurrencyCode} name={`${params.row.fromCurrencyCode}`}/>
    },
    {
      field: 'toCurrencyCode',
      headerName: 'To Currency',
      width: 120,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.toCurrencyCode} name={`${params.row.toCurrencyCode}`}/>
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 50,
      editable: false,
      renderCell: params => <InstructionStateChip state={params.row.state}/>
    },
    { field: 'assetHolderId', headerName: 'Member ID', flex: 50, editable: false },
    { field: 'assetHolderEmailAddress', headerName: 'Email address', flex: 80, editable: false },
    { field: 'fromCurrencyAmountFormatted', headerName: 'From Amount', flex: 50, editable: false, align: 'right' },
    { field: 'toCurrencyAmountFormatted', headerName: 'To Amount', flex: 50, editable: false, align: 'right' },
    { field: 'exchangeRate', headerName: 'Exchange Rate', flex: 50, editable: false, align: 'right' }
    // { field: 'feeAmountFormatted', headerName: 'Fee Amount', flex: 50, editable: false, align: 'right' },
    // { field: 'payoutAmountFormatted', headerName: 'Payout Amount', flex: 50, editable: false, align: 'right' },
    // { field: 'confirmedAtFormatted', headerName: 'Confirmed At', flex: 50, editable: false },
  ]

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography variant={'h4'}>Currency Exchange Instructions</Typography>
        <Button variant={'text'} type="button" onClick={() => { goTo('/app/finance-area') }}>
          <HomeIcon style={{ color: '#444' }} />
        </Button>
      </Box>

      <Paper>
        <Grid container justifyContent="right" sx={{ pr: 4, pt: 2 }}>
          <Grid item>
            <Button variant={'contained'} type="button" onClick={handleRefetch}>
              <Refresh/>
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ pl: 4, pr: 4, pt: 2 }}>
          <h3>Open Currency Exchange Batches</h3>
          { getOpenBatchesApiResult.isLoading && <LoadingComponent />}
          { getOpenBatchesApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getOpenBatchesApiResult.error)} />}
          { getOpenBatchesApiResult.isSuccess && (
            <DataGrid
              getRowId={(row) => row.batchId}
              rows={getOpenBatchesApiResult.data}
              columns={batchColumns}
              autoHeight={true}

              pagination
              pageSize={5}
              rowsPerPageOptions={[5]}
            />
          )}
        </Box>

        <Box sx={{ pl: 4, pr: 4, pt: 2 }}>
          Show All Batches
          <Switch
            checked={showAllBatches}
            onChange={handleBatchFilterSwitch}
            color="primary"
          />
        </Box>

        <Box sx={{ pl: 4, pr: 4, pt: 2, pb: 2 }}>
          <h3>Create Currency Exchange Instruction Batch</h3>
           { getPendingInstructionsApiResult.isLoading && <LoadingComponent />}
           { getPendingInstructionsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getPendingInstructionsApiResult.error)} />}
           { getPendingInstructionsApiResult.isSuccess && (
             <CreateBatch pendingInstructions={getPendingInstructionsApiResult.data} />
           )}
        </Box>

        <Box sx={{ pl: 4, pr: 4, pt: 2, pb: 2 }}>
          <h3>Pending Currency Exchange Instructions</h3>
          { getPendingInstructionsApiResult.isLoading && <LoadingComponent />}
          { getPendingInstructionsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getPendingInstructionsApiResult.error)} />}
          { getPendingInstructionsApiResult.isSuccess && (
            <DataGrid
              getRowId={(row) => row.currencyExchangeInstructionId}
              rows={getPendingInstructionsApiResult.data}
              columns={instructionColumns}
              autoHeight={true}
              disableSelectionOnClick

              pagination
              pageSize={15}
              rowsPerPageOptions={[15]}
            />
          )}
        </Box>
      </Paper>
    </>
  )
}

export default ExchangeInstructionBatchesPage
